// src/components/DealsMap.tsx
import React, { useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { FaBolt, FaExternalLinkAlt } from 'react-icons/fa';

// Fonction pour créer un marqueur HTML personnalisé (le petit badge avec le prix)
const createCustomIcon = (deal: any) => {
  const bgColor = deal.source === 'last_call' ? 'bg-red-600' : 'bg-slate-900';
  const priceText = deal.price ? `${deal.price}$` : '🔥';
  const html = `
    <div style="transform: translate(-50%, -100%);" class="${bgColor} text-white px-3 py-1.5 rounded-full text-xs font-black shadow-lg border-2 border-white inline-block whitespace-nowrap">
      ${priceText}
    </div>
  `;
  
  return L.divIcon({
    html: html,
    className: 'custom-leaflet-marker', // On retire les styles par défaut de Leaflet
    iconSize: [0, 0], // Géré par le HTML
    iconAnchor: [0, 0]
  });
};

export const DealsMap = ({ deals, onNavigate }: any) => {
  // On ne garde que les offres avec des coordonnées
  const mappableDeals = useMemo(() => {
    const safeDeals = Array.isArray(deals) ? deals : [];
    return safeDeals.filter((d: any) => d.lat != null && d.lng != null);
  }, [deals]);

  return (
    <div className="w-full h-[600px] rounded-3xl overflow-hidden shadow-lg border border-slate-200 relative z-0">
      <MapContainer 
        center={[46.8139, -71.2080]} // Québec par défaut
        zoom={10} 
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
      >
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
        />

        {mappableDeals.map((d: any) => (
          <Marker 
            key={`${d.source}:${d.source_id}`}
            position={[d.lat, d.lng]}
            icon={createCustomIcon(d)}
          >
            <Popup className="rounded-xl overflow-hidden">
              <div className="w-[220px] flex flex-col gap-3 p-1 m-[-10px]">
                {d.image_url && (
                  <img src={d.image_url} className="w-full h-28 object-cover rounded-t-xl" alt="" />
                )}
                <div className="px-2 pb-2">
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">{d.region || "Québec"}</div>
                  <h3 className="font-black text-slate-900 leading-tight line-clamp-2">{d.title}</h3>
                  <div className="text-lg font-black mt-2">{d.price ? `${d.price}$` : "—"}</div>
                  <button
                    onClick={(e) => { e.stopPropagation(); onNavigate(d); }}
                    className="mt-3 w-full bg-slate-900 text-white py-2 rounded-xl font-bold text-xs uppercase flex items-center justify-center gap-2 hover:bg-blue-600 transition"
                  >
                    {d.source === 'last_call' ? <><FaBolt /> Voir flash</> : <><FaExternalLinkAlt /> Découvrir</>}
                  </button>
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
      
      {/* Indicateur si certaines offres n'ont pas de position */}
      {(deals?.length || 0) > mappableDeals.length && (
        <div className="absolute top-4 left-4 bg-white/90 backdrop-blur px-3 py-1.5 rounded-full text-[10px] font-bold text-slate-600 shadow-sm z-[1000]">
          {(deals?.length || 0) - mappableDeals.length} offre(s) sans position GPS
        </div>
      )}
    </div>
  );
};
